import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, InputNumber, Input, Checkbox, Alert, message } from "antd";
import axios from "axios";
import PropTypes from "prop-types";
import { formatAmount, roundToCents } from "../../helpers/billingStatement";

const BillingBankTransferModal = ({ statement, onClose, onRecorded }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  // Set when the API reports a gateway payment that may still be in progress (409 + meta).
  const [gatewayPending, setGatewayPending] = useState(false);
  // Set synchronously so a double-click can't send twice before validation resolves.
  const submittingRef = useRef(false);
  const statementIdRef = useRef();
  statementIdRef.current = statement?.objectId;

  useEffect(() => {
    setGatewayPending(false);
    if (!statement) return;
    // initialValues only seed the first mount, so set the amount for each invoice explicitly.
    form.resetFields();
    form.setFieldsValue({ amount: roundToCents(statement.outstandingAmount) });
  }, [statement, form]);

  const handleOk = async () => {
    if (!statement || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    const statementId = statement.objectId;
    try {
      let values;
      try {
        values = await form.validateFields();
      } catch {
        return;
      }

      await axios.post("/api/billing-bank-transfers", {
        statementId,
        amount: values.amount,
        reference: values.reference.trim(),
        ...(values.remark && { remark: values.remark.trim() }),
        ...(values.confirmNoGatewayPayment && { confirmNoGatewayPayment: true }),
      });
      message.success("Bank transfer recorded. The merchant is not unblocked automatically.");
      onRecorded();
    } catch (error) {
      // The modal may have moved on to another invoice; don't apply this reply to it.
      if (statementIdRef.current === statementId) {
        if (error.response?.status === 409 && error.response?.data?.meta?.gatewayIntentId) {
          setGatewayPending(true);
        }
      }
      message.error(error.response?.data?.message || error.message);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const outstanding = roundToCents(statement?.outstandingAmount);

  return (
    <Modal
      title={`Record bank transfer${statement ? ` - ${statement.statementNumber}` : ""}`}
      visible={!!statement}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={submitting}
      cancelButtonProps={{ disabled: submitting }}
      maskClosable={!submitting}
      keyboard={!submitting}
      closable={!submitting}
      okText="Record"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {statement && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Outstanding: ${formatAmount(outstanding, statement.currency)}`}
            description="Recording a transfer takes effect immediately and cannot be voided. It does not unblock the merchant."
          />
        )}
        <Form.Item
          label="Amount"
          name="amount"
          rules={[
            { required: true, message: "Enter the amount received" },
            { type: "number", min: 0.01, max: outstanding, message: `Must be between 0.01 and ${outstanding}` },
          ]}
        >
          <InputNumber min={0} precision={2} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          label="Bank reference"
          name="reference"
          rules={[
            { required: true, whitespace: true, message: "Enter the bank reference" },
            { max: 100, message: "At most 100 characters" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Remark (optional)" name="remark" rules={[{ max: 200, message: "At most 200 characters" }]}>
          <Input.TextArea rows={2} />
        </Form.Item>
        {gatewayPending && (
          <Form.Item
            name="confirmNoGatewayPayment"
            valuePropName="checked"
            rules={[{ validator: (_, v) => (v ? Promise.resolve() : Promise.reject(new Error("Confirm to continue"))) }]}
          >
            <Checkbox>I have checked with Paydibs that no gateway payment is pending for this invoice</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

BillingBankTransferModal.propTypes = {
  statement: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onRecorded: PropTypes.func.isRequired,
};

export default BillingBankTransferModal;
